/**
 * EventPicker — the one event selector for forms.
 *
 * Type-ahead over the server (title / venue / city, a date such as 2026-11-14
 * or Nov 14, or an event id / id prefix), grouped Upcoming (soonest first) then
 * Past (most recent first). Each group pages with "Load more" (keyset cursor),
 * so every event is reachable — including the 26th of 26 identically-named
 * recurring events. Options show date + time + venue + short id so identical
 * names can be told apart. The saved selection is loaded by id, so an edit form
 * keeps showing an event that is not in the current results.
 *
 * Replaces fixed-window dropdowns that hid every event past row 100/200/500.
 */
import React, { useEffect, useId, useState } from "react";
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  getEventPickerOptionById,
  searchEventPickerGroup,
  type EventPickerCursor,
  type EventPickerGroup,
} from "../../services/eventPicker.service";
import {
  eventPickerShortId,
  eventPickerStatusNote,
  formatEventPickerDateTime,
  formatEventPickerLabel,
  formatEventPickerPlace,
  type EventPickerOption,
} from "../../utils/eventPicker";

export interface EventPickerProps {
  /** Selected event id, or "" for none. */
  value: string;
  onChange: (eventId: string, event: EventPickerOption | null) => void;
  /** Visible label. Omit when the caller renders its own. */
  label?: React.ReactNode;
  required?: boolean;
  /** When set, the selection can be cleared and this text describes "no event". */
  noneLabel?: string;
  error?: string;
  helperText?: string;
  disabled?: boolean;
  /** id for the search input (lets an outer <label htmlFor> point at it). */
  inputId?: string;
  /** Prefix for data-testid hooks. */
  testId?: string;
}

const SEARCH_DEBOUNCE_MS = 250;

const eventPickerKeys = {
  search: (term: string, group: EventPickerGroup) =>
    ["event-picker", "search", group, term] as const,
  byId: (id: string) => ["event-picker", "by-id", id] as const,
};

// One infinite (keyset-paged) query per group. A new term is a new key, so
// changing the search always starts again from the first page.
function useEventPickerGroup(
  term: string,
  group: EventPickerGroup,
  enabled: boolean,
) {
  return useInfiniteQuery({
    queryKey: eventPickerKeys.search(term, group),
    queryFn: ({ pageParam }) =>
      searchEventPickerGroup(term, group, { cursor: pageParam }),
    initialPageParam: null as EventPickerCursor | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled,
    staleTime: 30 * 1000,
  });
}

// An event whose start_time is edited between two page loads could appear on
// both pages; keep the first occurrence so ids (and React keys) stay unique.
function dedupeById(rows: EventPickerOption[]): EventPickerOption[] {
  const seen = new Set<string>();
  return rows.filter((r) => (seen.has(r.id) ? false : (seen.add(r.id), true)));
}

const EventPicker: React.FC<EventPickerProps> = ({
  value,
  onChange,
  label,
  required,
  noneLabel,
  error,
  helperText,
  disabled,
  inputId,
  testId = "event-picker",
}) => {
  const queryClient = useQueryClient();
  const autoId = useId();
  const searchId = inputId ?? `event-picker-${autoId}`;
  const listId = `${searchId}-list`;

  // Search panel is open when nothing is selected, or the user chose "Change".
  const [changing, setChanging] = useState(false);
  const [term, setTerm] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const h = setTimeout(() => setDebounced(term.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(h);
  }, [term]);

  const showSearch = !value || changing;

  // A new value from the parent closes a stale "Change" panel.
  useEffect(() => {
    setChanging(false);
  }, [value]);

  const selected = useQuery({
    queryKey: eventPickerKeys.byId(value),
    queryFn: () => getEventPickerOptionById(value),
    enabled: !!value,
    staleTime: 60 * 1000,
    retry: false,
  });

  const upcoming = useEventPickerGroup(
    debounced,
    "upcoming",
    showSearch && !disabled,
  );
  const past = useEventPickerGroup(debounced, "past", showSearch && !disabled);

  const pick = (ev: EventPickerOption) => {
    // Seed the by-id cache so the selected card renders without a refetch.
    queryClient.setQueryData(eventPickerKeys.byId(ev.id), ev);
    setChanging(false);
    setTerm("");
    onChange(ev.id, ev);
  };

  const clear = () => {
    setChanging(false);
    setTerm("");
    onChange("", null);
  };

  const renderOption = (ev: EventPickerOption) => {
    const when = formatEventPickerDateTime(ev.start_time);
    const venue = (ev.location_name ?? "").trim();
    const place = formatEventPickerPlace(ev);
    const note = eventPickerStatusNote(ev);
    const isSelected = ev.id === value;
    return (
      <li key={ev.id} role="presentation">
        <button
          type="button"
          role="option"
          aria-selected={isSelected}
          data-testid={`${testId}-option`}
          data-event-id={ev.id}
          onClick={() => pick(ev)}
          className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 focus:bg-blue-50 focus:outline-none ${
            isSelected ? "bg-blue-50" : ""
          }`}
        >
          <span className="block font-medium text-gray-900 truncate">
            {(ev.title ?? "").trim() || "Untitled event"}
            {note && (
              <span className="ml-2 text-xs font-normal text-red-600">
                ({note})
              </span>
            )}
          </span>
          <span className="block text-xs text-gray-500">
            {[when, venue, place, `ID ${eventPickerShortId(ev.id)}`]
              .filter(Boolean)
              .join(" · ")}
          </span>
        </button>
      </li>
    );
  };

  const upRows = dedupeById(upcoming.data?.pages.flatMap((p) => p.rows) ?? []);
  const pastRows = dedupeById(past.data?.pages.flatMap((p) => p.rows) ?? []);
  const loaded = !!upcoming.data && !!past.data;
  const failed = upcoming.isError || past.isError;
  const empty = loaded && upRows.length === 0 && pastRows.length === 0;
  const listShown = loaded && !failed && !empty;

  const loadMore = (
    group: EventPickerGroup,
    q: typeof upcoming,
    rowsShown: number,
  ) =>
    q.hasNextPage ? (
      <li role="presentation" className="px-3 py-1">
        <button
          type="button"
          onClick={() => q.fetchNextPage()}
          disabled={q.isFetchingNextPage}
          data-testid={`${testId}-more-${group}`}
          className="text-xs font-medium text-blue-600 hover:text-blue-800 disabled:text-gray-400"
        >
          {q.isFetchingNextPage
            ? "Loading…"
            : `Load more ${group} events (${rowsShown} shown)`}
        </button>
      </li>
    ) : null;

  return (
    <div data-testid={testId}>
      {label && (
        <label
          htmlFor={searchId}
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {value && (
        <div
          data-testid={`${testId}-selected`}
          className={`flex items-start gap-2 px-3 py-2 border rounded-lg text-sm bg-gray-50 ${
            error ? "border-red-500" : "border-gray-300"
          } ${showSearch ? "mb-2" : ""}`}
        >
          <span className="flex-1 min-w-0">
            {selected.isLoading ? (
              <span className="text-gray-500">Loading selected event…</span>
            ) : selected.data ? (
              <span className="block truncate text-gray-900">
                {formatEventPickerLabel(selected.data)}
              </span>
            ) : (
              <span className="text-amber-700">
                Linked event could not be loaded (id {value}). Pick another
                event{noneLabel ? " or clear it" : ""}.
              </span>
            )}
          </span>
          {!disabled && (
            <>
              <button
                type="button"
                onClick={() => setChanging((c) => !c)}
                className="shrink-0 text-blue-600 hover:text-blue-800 font-medium"
                data-testid={`${testId}-change`}
              >
                {changing ? "Cancel" : "Change"}
              </button>
              {noneLabel && (
                <button
                  type="button"
                  onClick={clear}
                  className="shrink-0 text-gray-500 hover:text-gray-800"
                  data-testid={`${testId}-clear`}
                >
                  Clear
                </button>
              )}
            </>
          )}
        </div>
      )}

      {!value && noneLabel && (
        <p className="mb-1 text-xs text-gray-500">{noneLabel}</p>
      )}

      {showSearch && !disabled && (
        <div>
          <input
            id={searchId}
            type="text"
            role="combobox"
            aria-expanded={listShown}
            aria-controls={listShown ? listId : undefined}
            aria-autocomplete="list"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            // Enter must never submit the surrounding form (CampaignCreate, SequenceCreate,
            // TaskForm): it would save with the OLD or no event while the user is searching.
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
              }
            }}
            placeholder="Search by name, venue, city, date or event ID…"
            data-testid={`${testId}-search`}
            className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              error && !value ? "border-red-500" : "border-gray-300"
            }`}
          />
          <div
            className="mt-1 max-h-64 overflow-y-auto border border-gray-200 rounded-lg bg-white"
            aria-busy={upcoming.isFetching || past.isFetching}
          >
            {failed ? (
              <p className="px-3 py-2 text-sm text-red-600">
                Events could not be loaded. Try again.
              </p>
            ) : !loaded ? (
              <p className="px-3 py-2 text-sm text-gray-500">Loading events…</p>
            ) : empty ? (
              <p className="px-3 py-2 text-sm text-gray-500">
                {debounced
                  ? `No events match "${debounced}".`
                  : "No events yet."}
              </p>
            ) : (
              <ul id={listId} role="listbox" aria-label="Events">
                {upRows.length > 0 && (
                  <li
                    role="presentation"
                    className="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500"
                  >
                    Upcoming
                  </li>
                )}
                {upRows.map(renderOption)}
                {loadMore("upcoming", upcoming, upRows.length)}
                {pastRows.length > 0 && (
                  <li
                    role="presentation"
                    className="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 border-t border-gray-100"
                  >
                    Past
                  </li>
                )}
                {pastRows.map(renderOption)}
                {loadMore("past", past, pastRows.length)}
              </ul>
            )}
          </div>
        </div>
      )}

      {showSearch && !disabled && (
        <p className="mt-1 text-xs text-gray-500">
          Tip: search a date (2026-11-14, 11/14 or Nov 14) or an event ID to
          pick one of several events with the same name.
        </p>
      )}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
      {helperText && !error && (
        <p className="mt-1 text-sm text-gray-500">{helperText}</p>
      )}
    </div>
  );
};

export default EventPicker;
